import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, Send, Download, Receipt, Trash2, Mail, MessageSquare, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Quotes() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [sendFor, setSendFor] = useState(null);
  const [sendForm, setSendForm] = useState({ channel: "email", to: "", message: "" });

  const load = async () => setRows((await api.get("/quotes")).data);
  useEffect(() => { load(); }, []);

  const openSend = async (q) => {
    const c = (await api.get(`/customers/${q.customer_id}`)).data;
    const link = `${window.location.origin}/quote/${q.id}`;
    setSendFor(q);
    setSendForm({
      channel: "email",
      to: c.email || c.phone || "",
      message: `Hi ${c.name},\n\nHere is your quote ${q.number} from us for ${q.title}. Total: ${fmt(q.total)}.\n\nView & accept: ${link}`,
    });
  };

  const doSend = async () => {
    try {
      await api.post("/communications/send", { ...sendForm, subject: `Your quote ${sendFor.number}`, customer_id: sendFor.customer_id, entity_type: "quote", entity_id: sendFor.id });
      await api.patch(`/quotes/${sendFor.id}/status`, { status: "sent" });
      toast.success(`Quote sent via ${sendForm.channel}`);
      setSendFor(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toInvoice = async (q) => {
    try {
      const { data } = await api.post(`/invoices/from-quote/${q.id}`);
      toast.success(`Invoice ${data.number} created`);
      nav(`/dashboard/invoices/${data.id}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (q) => {
    if (!window.confirm(`Delete quote ${q.number}?`)) return;
    await api.delete(`/quotes/${q.id}`); load();
  };

  const dlPdf = (q) => window.open(`${API}/quotes/${q.id}/pdf`, "_blank");

  return (
    <div data-testid="quotes-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Quotes</h1>
          <p className="text-sm text-slate-500 mt-1">Create, send and track every estimate.</p>
        </div>
        <Button data-testid="new-quote-btn" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => nav("/dashboard/quotes/new")}>
          <Plus className="w-4 h-4 mr-1.5" /> New Quote
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="quotes-table">
          <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-3">Number</th>
              <th className="text-left px-5 py-3">Customer</th>
              <th className="text-left px-5 py-3">Title</th>
              <th className="text-right px-5 py-3">Total</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Created</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500">No quotes yet. <button onClick={() => nav("/dashboard/quotes/new")} className="text-blue-600 font-medium">Create one</button></td></tr>}
            {rows.map((q) => (
              <tr key={q.id} className="odd:bg-slate-50/50 border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono font-semibold text-slate-900"><button onClick={() => nav(`/dashboard/quotes/${q.id}`)} className="hover:text-blue-600">{q.number}</button></td>
                <td className="px-5 py-3">{q.customer_name}</td>
                <td className="px-5 py-3 text-slate-600 max-w-[220px] truncate">{q.title}</td>
                <td className="px-5 py-3 text-right font-medium text-slate-900">{fmt(q.total)}</td>
                <td className="px-5 py-3"><span className={`status-pill status-${q.status}`}>{q.status}</span></td>
                <td className="px-5 py-3 text-slate-600">{new Date(q.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button title="Send" data-testid={`send-quote-${q.id}`} onClick={() => openSend(q)} className="p-1.5 hover:bg-slate-100 rounded"><Send className="w-4 h-4 text-blue-600" /></button>
                  <button title="PDF" data-testid={`pdf-quote-${q.id}`} onClick={() => dlPdf(q)} className="p-1.5 hover:bg-slate-100 rounded"><Download className="w-4 h-4 text-slate-600" /></button>
                  <button title="Public link" onClick={() => window.open(`/quote/${q.id}`, "_blank")} className="p-1.5 hover:bg-slate-100 rounded"><ExternalLink className="w-4 h-4 text-slate-600" /></button>
                  {q.status === "accepted" && <button title="Convert" data-testid={`convert-${q.id}`} onClick={() => toInvoice(q)} className="p-1.5 hover:bg-slate-100 rounded"><Receipt className="w-4 h-4 text-emerald-600" /></button>}
                  <button title="Delete" onClick={() => del(q)} className="p-1.5 hover:bg-slate-100 rounded"><Trash2 className="w-4 h-4 text-red-600" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!sendFor} onOpenChange={(v) => !v && setSendFor(null)}>
        <DialogContent className="max-w-lg" data-testid="send-quote-dialog">
          <DialogHeader><DialogTitle>Send quote {sendFor?.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Channel</Label>
              <Select value={sendForm.channel} onValueChange={(v) => setSendForm({ ...sendForm, channel: v })}>
                <SelectTrigger data-testid="send-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email"><Mail className="w-3 h-3 inline mr-1" />Email</SelectItem>
                  <SelectItem value="sms"><MessageSquare className="w-3 h-3 inline mr-1" />Text (SMS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">To</Label>
              <Input
                data-testid="send-to"
                type={sendForm.channel === "email" ? "email" : "text"}
                placeholder={sendForm.channel === "email" ? "customer@example.com" : "+1 555-123-4567"}
                value={sendForm.to}
                onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
              />
              {sendForm.channel === "email" && !sendForm.to && (
                <p className="mt-1 text-[11px] text-amber-700 font-semibold" data-testid="send-to-warning">
                  This customer has no email on file. Type one above and we'll save it to their record.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Message</Label>
              <Textarea data-testid="send-message" rows={6} value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendFor(null)}>Cancel</Button>
            <Button data-testid="send-confirm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={doSend}><Send className="w-4 h-4 mr-1.5" /> Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
